import type { Placement } from "@floating-ui/react";
import {
    arrow,
    autoUpdate,
    flip,
    offset,
    shift,
    useDismiss,
    useFloating,
    useFocus,
    useHover,
    useInteractions,
    useRole,
} from "@floating-ui/react";
import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

export const SHIFT = 5; // Same as in floater
export const OFFSET = 10;

export type TooltipOptions = {
    initialOpen?: boolean;
    placement?: Placement;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
};
// We should not hard type it as Typescript is smart enough to do this
// later on
// eslint-disable-next-line
export const useTooltip = ({
    initialOpen = false,
    placement = "bottom",
    open: controlledOpen,
    onOpenChange: setControlledOpen,
}: TooltipOptions = {}) => {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);

    const open = controlledOpen ?? uncontrolledOpen;
    const setOpen = setControlledOpen ?? setUncontrolledOpen;
    const arrowRef = useRef<HTMLDivElement | null>(null);

    const data = useFloating({
        placement,
        open,
        onOpenChange: setOpen,
        whileElementsMounted: autoUpdate,
        middleware: [offset(OFFSET), flip(), shift({ padding: SHIFT }), arrow({ element: arrowRef })],
    });

    const arrowCallback = useCallback(
        (node: HTMLDivElement | null) => {
            arrowRef.current = node;
            data.update();
        },
        [data],
    );

    const { context } = data;

    const hover = useHover(context, {
        move: false,
        enabled: controlledOpen == null,
    });
    const focus = useFocus(context, {
        enabled: controlledOpen == null,
    });
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: "tooltip" });

    const interactions = useInteractions([hover, focus, dismiss, role]);

    return useMemo(
        () => ({
            open,
            setOpen,
            arrowCallback,
            ...interactions,
            ...data,
        }),
        [open, setOpen, arrowCallback, interactions, data],
    );
};

type ContextType = ReturnType<typeof useTooltip> | null;

export const TooltipContext = createContext<ContextType>(null);

export const useTooltipContext = (): ContextType => {
    const context = useContext(TooltipContext);

    if (context == null) {
        throw new Error("Tooltip components must be wrapped in <Tooltip />");
    }

    return context;
};
